/**
 * Rich response card for enquiry_list: summary stats, filters, table with row actions.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import SummaryCards from "./SummaryCards";
import EnquiryList from "../AIChat/AIComponents/EnquiryList";
import styles from "./FlashAssistant.module.scss";

function computeStats(data = []) {
  const total = data.length;
  const sourceCounts = {};
  let withApplication = 0;
  data.forEach((e) => {
    const src = e.source || "Unknown";
    sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    if (e.status && String(e.status).toLowerCase().includes("application")) withApplication += 1;
  });
  const topSource =
    Object.keys(sourceCounts).length > 0
      ? Object.entries(sourceCounts).sort((a, b) => b[1] - a[1])[0][0]
      : "—";
  const conversionRate = total > 0 ? Math.round((withApplication / total) * 100) : 0;
  return {
    totalEnquiries: total,
    topSource,
    conversionRate: total > 0 ? `${conversionRate}%` : "—",
    applicationsSubmitted: withApplication,
  };
}

export default function EnquiryResponseCard({ data = [], title = "Here are the enquiries I found" }) {
  const navigate = useNavigate();
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const stats = useMemo(() => computeStats(data), [data]);

  const filteredData = useMemo(() => {
    let list = [...(data || [])];
    if (sourceFilter) {
      list = list.filter((e) => (e.source || "").toLowerCase() === sourceFilter.toLowerCase());
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((e) => e.time_created && new Date(e.time_created) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((e) => e.time_created && new Date(e.time_created) <= to);
    }
    return list;
  }, [data, sourceFilter, dateFrom, dateTo]);

  const sources = useMemo(() => {
    const set = new Set((data || []).map((e) => e.source || "Unknown").filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const handleOpenProfile = (enquiry) => {
    navigate("/enquiry_profile", { state: { enquiry } });
  };

  return (
    <div className={styles.responseCard}>
      <div className={styles.responseCardTitle}>{title}</div>
      <SummaryCards
        totalEnquiries={stats.totalEnquiries}
        topSource={stats.topSource}
        conversionRate={stats.conversionRate}
        applicationsSubmitted={stats.applicationsSubmitted}
      />
      <div className={styles.filters}>
        <span className={styles.filtersLabel}>Filters:</span>
        <Form.Select
          size="sm"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={{ width: 160 }}
        >
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          type="date"
          size="sm"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{ width: 140 }}
          placeholder="From"
        />
        <Form.Control
          type="date"
          size="sm"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{ width: 140 }}
          placeholder="To"
        />
      </div>
      <div className={styles.tableContainer}>
        <EnquiryList data={filteredData} />
      </div>
      <div className={`${styles.rowActions} mt-2`}>
        <button type="button" className={styles.rowActionBtn}>
          Message
        </button>
        <button type="button" className={styles.rowActionBtn}>
          Add Note
        </button>
        <button type="button" className={styles.rowActionBtn}>
          Open Profile
        </button>
        <button type="button" className={styles.rowActionBtn}>
          Export CSV
        </button>
      </div>
    </div>
  );
}
